// src/scenes/incomes/Incomes.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Modal,
    IconButton,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    useTheme,
    List,
    ListItem,
    ListItemText,
    Divider,
    Alert,
    Stack,
    Chip,
    Badge
} from "@mui/material";
import { DataGridPro, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton, GridToolbarColumnsButton, GridToolbarDensitySelector } from "@mui/x-data-grid-pro";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { alpha, lighten } from "@mui/material/styles";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UploadIcon from "@mui/icons-material/Upload";
import AddIcon from "@mui/icons-material/Add";
import DescriptionIcon from "@mui/icons-material/Description";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import IncomeEntry from "../../component-files/Forms/IncomeEntry";

const BASE = "https://figazuresql.swancreate.com/api/swa";
const API_INCOMES = `${BASE}/FINANCE_Incomes`;
const API_DEPOSIT_MST = `${BASE}/FINANCE_DepositMst`;
const API_DEPOSIT_DETAILS = `${BASE}/FINANCE_DepositDetails`;
const API_CATEGORIES = `${BASE}/finance_categories`;
const API_SUBCATEGORIES = `${BASE}/finance_subcategories`;
const API_SCHOOLS = `${BASE}/SchoolMst`;
const API_REIMBURSE_PROGS = `${BASE}/FINANCE_ReimbursementPrograms`;

const AI_BASE = process.env.REACT_APP_AI_BASE || "https://gpt5api.swancreate.com";
const API_DI_EXTRACT = `${AI_BASE}/v1/di/extract`;
const API_CHAT = `${AI_BASE}/v1/chat`;

const haptic = {
    light: () => { if (navigator.vibrate) navigator.vibrate(10); },
    medium: () => { if (navigator.vibrate) navigator.vibrate(20); },
    heavy: () => { if (navigator.vibrate) navigator.vibrate(40); },
    success: () => { if (navigator.vibrate) navigator.vibrate([10, 50, 10]); },
    error: () => { if (navigator.vibrate) navigator.vibrate([20, 100, 20, 100, 20]); },
    selection: () => { if (navigator.vibrate) navigator.vibrate(5); }
};

const getCurrentSYStart = (today = new Date()) =>
    today.getMonth() >= 6 ? today.getFullYear() : today.getFullYear() - 1;
const getSYStartFromDate = (d) => (d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1);
const inSchoolYear = (d, startYear) =>
    d >= new Date(startYear, 6, 1) && d < new Date(startYear + 1, 6, 1);
const syLabel = (startYear) => `${startYear}-${startYear + 1}`;

const usd = (v) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v || 0));
const parseMaybeNumber = (x) => {
    if (x == null) return 0;
    const n = typeof x === "number" ? x : parseFloat(String(x).replace(/[,$\s]/g, ""));
    return Number.isFinite(n) ? n : 0;
};
const normalizeDate = (s) => {
    if (!s) return "";
    try {
        const d = new Date(s);
        if (isNaN(d.getTime())) return "";
        return d.toISOString().slice(0, 10);
    } catch {
        return "";
    }
};
const stripJson = (text) => {
    if (!text) return null;
    const direct = text.trim();
    if (direct.startsWith("{") || direct.startsWith("[")) {
        try { return JSON.parse(direct); } catch { }
    }
    const fence = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
    if (fence) { try { return JSON.parse(fence[1]); } catch { } }
    const start = text.indexOf("{");
    const end = text.lastIndexOf("}");
    if (start >= 0 && end > start) { try { return JSON.parse(text.slice(start, end + 1)); } catch { } }
    return null;
};

function CustomToolbar() {
    return (
        <GridToolbarContainer>
            <GridToolbarColumnsButton />
            <GridToolbarFilterButton />
            <GridToolbarDensitySelector />
            <GridToolbarExport
                csvOptions={{ fileName: `incomes-export-${new Date().toISOString().slice(0, 10)}` }}
                printOptions={{ disableToolbarButton: true }}
            />
        </GridToolbarContainer>
    );
}

export default function Incomes() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [incomes, setIncomes] = useState([]);
    const [cats, setCats] = useState([]);
    const [subcats, setSubcats] = useState([]);
    const [schools, setSchools] = useState([]);
    const [progs, setProgs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [syStart, setSyStart] = useState(getCurrentSYStart());
    const [monthFilter, setMonthFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [subcategoryFilter, setSubcategoryFilter] = useState("");
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [schoolFilter, setSchoolFilter] = useState("");

    const [entryOpen, setEntryOpen] = useState(false);
    const [depositOpen, setDepositOpen] = useState(false);
    const [scanOpen, setScanOpen] = useState(false);

    const [selectedRows, setSelectedRows] = useState([]);
    const [pinnedColumns, setPinnedColumns] = useState({ left: ['SiteName'] });

    useEffect(() => {
        loadLookups();
        loadIncomes();
    }, []);

    async function loadLookups() {
        try {
            const [c, sc, s, p] = await Promise.all([
                fetch(API_CATEGORIES).then((r) => r.json()),
                fetch(API_SUBCATEGORIES).then((r) => r.json()),
                fetch(API_SCHOOLS).then((r) => r.json()),
                fetch(API_REIMBURSE_PROGS).then((r) => r.json())
            ]);
            s.sort((a, b) => a.SchoolName.localeCompare(b.SchoolName));
            setCats((c || []).filter((x) => x.Type === "Income"));
            setSubcats(sc || []);
            setSchools(s || []);
            setProgs(p || []);
            haptic.light();
        } catch (e) {
            console.error("Lookup load failed", e);
            setError("Lookup load failed");
            haptic.error();
        }
    }

    async function loadIncomes() {
        setLoading(true);
        try {
            const data = await fetch(API_INCOMES).then((r) => r.json());
            console.log("📊 Sample income record:", data[0]);
            setIncomes(data || []);
            haptic.light();
        } catch (e) {
            console.error("Income load failed", e);
            setError("Income load failed");
            haptic.error();
        } finally {
            setLoading(false);
        }
    }

    const handleDelete = useCallback(async (id) => {
        haptic.medium();
        try {
            await fetch(`${API_INCOMES}/${id}`, { method: "DELETE" });
            setIncomes((ins) => ins.filter((r) => r.apiid !== id));
            haptic.success();
        } catch (e) {
            console.error("Delete failed", e);
            setError("Delete failed");
            haptic.error();
        }
    }, []);

    const handleCellEditCommit = useCallback(async (params) => {
        const { id: rowApiid, field, value } = params;
        let body = {};
        haptic.light();

        if (field === "SiteName") {
            const school = schools.find((s) => s.SchoolName === value);
            if (!school) { setError("Unknown site selected."); haptic.error(); return; }
            body.SchoolId = school.apiid;
            body.SiteName = value?.trim();
        } else if (field === "IncomeCategory") {
            const cat = cats.find((c) => c.CategoryName === value);
            if (cat) {
                body.IncomeCategory = cat.CategoryName;
                body.IncomeCategoryID = cat.CategoryID;
                const currentSub = subcats.find(
                    (sc) => sc.SubcategoryName === params.row.IncomeSubcategory && sc.CategoryID === cat.CategoryID
                );
                if (!currentSub) { body.IncomeSubcategory = ""; body.IncomeSubCategoryID = null; }
            } else {
                body[field] = value;
            }
        } else if (field === "IncomeSubcategory") {
            const parentCat = cats.find((c) => c.CategoryID === params.row.IncomeCategoryID);
            const sc = subcats.find((s) => s.SubcategoryName === value && s.CategoryID === parentCat?.CategoryID);
            if (sc) {
                body.IncomeSubcategory = sc.SubcategoryName;
                body.IncomeSubCategoryID = sc.SubcategoryID;
            } else { setError("Subcategory must belong to the selected Category."); haptic.error(); return; }
        } else {
            body[field] = value;
        }

        try {
            const res = await fetch(`${API_INCOMES}/${rowApiid}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body)
            });
            if (!res.ok) {
                const txt = await res.text();
                console.error("Update failed", res.status, txt);
                haptic.error();
            } else {
                setIncomes((prev) => prev.map((r) => (r.apiid === rowApiid ? { ...r, ...body } : r)));
                haptic.success();
            }
        } catch (e) {
            console.error("Error updating", e);
            haptic.error();
        }
    }, [schools, cats, subcats]);

    const processRowUpdate = useCallback((newRow) => {
        const site = (newRow.SiteName || "").trim();
        if (!site) throw new Error("Site (location) is required.");
        haptic.light();
        return newRow;
    }, []);

    const availableSYStarts = useMemo(() => {
        const years = new Set();
        (incomes || []).forEach((r) => {
            if (r?.IncomeDate) years.add(getSYStartFromDate(new Date(r.IncomeDate)));
        });
        const arr = Array.from(years).sort((a, b) => b - a);
        return arr.length ? arr : [getCurrentSYStart()];
    }, [incomes]);

    const availableSubcats = useMemo(() => {
        if (!categoryFilter) return [];
        return subcats.filter((sc) => String(sc.CategoryID) === String(categoryFilter));
    }, [subcats, categoryFilter]);

    const handleCategoryChange = useCallback((newCategory) => {
        setCategoryFilter(newCategory);
        if (newCategory && subcategoryFilter) {
            const isValid = subcats.some(
                (sc) => String(sc.SubcategoryID) === String(subcategoryFilter) &&
                    String(sc.CategoryID) === String(newCategory)
            );
            if (!isValid) {
                setSubcategoryFilter("");
            }
        }
        haptic.selection();
    }, [subcategoryFilter, subcats]);

    const filtered = useMemo(() => {
        console.log("🔍 Filtering with school:", schoolFilter);

        return (incomes || []).filter((r) => {
            if (!r.IncomeDate) return false;
            const d = new Date(r.IncomeDate);

            if (!inSchoolYear(d, syStart)) return false;

            if (monthFilter && d.toLocaleString("en-US", { month: "long" }) !== monthFilter) return false;

            if (categoryFilter && String(r.IncomeCategoryID) !== String(categoryFilter)) return false;

            if (subcategoryFilter && String(r.IncomeSubCategoryID) !== String(subcategoryFilter)) return false;

            // School filter - check both SchoolId and SiteName
            if (schoolFilter) {
                const selectedSchool = schools.find(s => String(s.apiid) === String(schoolFilter));
                const matchById = String(r.SchoolId) === String(schoolFilter);
                const matchByName = selectedSchool && r.SiteName === selectedSchool.SchoolName;

                console.log("School filter check:", {
                    recordSchoolId: r.SchoolId,
                    recordSiteName: r.SiteName,
                    filterValue: schoolFilter,
                    selectedSchoolName: selectedSchool?.SchoolName,
                    matchById,
                    matchByName
                });

                if (!matchById && !matchByName) return false;
            }

            if (dateFrom && d < dateFrom) return false;
            if (dateTo) {
                const endOfDay = new Date(dateTo);
                endOfDay.setHours(23, 59, 59, 999);
                if (d > endOfDay) return false;
            }

            return true;
        });
    }, [incomes, syStart, monthFilter, categoryFilter, subcategoryFilter, schoolFilter, dateFrom, dateTo, schools]);

    const months = useMemo(() => {
        return Array.from(
            new Set(
                (incomes || [])
                    .filter((r) => r.IncomeDate && inSchoolYear(new Date(r.IncomeDate), syStart))
                    .map((r) => new Date(r.IncomeDate).toLocaleString("en-US", { month: "long" }))
            )
        );
    }, [incomes, syStart]);

    const totalAmount = useMemo(() => {
        return filtered.reduce((sum, r) => sum + parseMaybeNumber(r.Amount), 0);
    }, [filtered]);

    const clearFilters = useCallback(() => {
        haptic.medium();
        setMonthFilter("");
        setCategoryFilter("");
        setSubcategoryFilter("");
        setSchoolFilter("");
        setDateFrom(null);
        setDateTo(null);
    }, []);

    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (monthFilter) count++;
        if (categoryFilter) count++;
        if (subcategoryFilter) count++;
        if (schoolFilter) count++;
        if (dateFrom) count++;
        if (dateTo) count++;
        return count;
    }, [monthFilter, categoryFilter, subcategoryFilter, schoolFilter, dateFrom, dateTo]);

    const mainCols = useMemo(() => [
        {
            field: "SiteName",
            headerName: "Site",
            flex: 1,
            minWidth: 150,
            editable: true,
            type: "singleSelect",
            valueOptions: schools.map((s) => s.SchoolName),
            preProcessEditCellProps: (params) => {
                const val = (params.props.value || "").trim();
                return { ...params.props, error: !val };
            }
        },
        {
            field: "IncomeCategory",
            headerName: "Category",
            flex: 1,
            minWidth: 140,
            editable: true,
            type: "singleSelect",
            valueOptions: cats.map((c) => c.CategoryName)
        },
        {
            field: "IncomeSubcategory",
            headerName: "Subcategory",
            flex: 1,
            minWidth: 140,
            editable: true,
            type: "singleSelect",
            valueOptions: (params) => {
                const cat = cats.find((c) => c.CategoryName === params.row.IncomeCategory);
                return subcats.filter((sc) => sc.CategoryID === cat?.CategoryID).map((sc) => sc.SubcategoryName);
            }
        },
        {
            field: "Amount",
            headerName: "Amount",
            flex: 0.8,
            minWidth: 100,
            editable: true,
            type: "number",
            valueFormatter: ({ value }) => usd(value),
            cellClassName: "amount-cell"
        },
        {
            field: "IncomeDate",
            headerName: "Date",
            flex: 0.8,
            minWidth: 110,
            editable: true,
            type: "date",
            valueGetter: ({ row }) => row.IncomeDate ? new Date(row.IncomeDate) : null,
            renderCell: ({ row }) => (row.IncomeDate ? new Date(row.IncomeDate).toLocaleDateString("en-US") : "N/A")
        },
        {
            field: "Description",
            headerName: "Description",
            flex: 1.2,
            minWidth: 200,
            editable: true
        },
        {
            field: "ReimbursementTrackingProgram",
            headerName: "Reimb Prog",
            flex: 0.8,
            minWidth: 120,
            editable: true,
            type: "singleSelect",
            valueOptions: ["", ...progs.map((p) => p.program_abbreviation).filter(Boolean)]
        },
        {
            field: "DepositDate",
            headerName: "Deposit Date",
            flex: 0.8,
            minWidth: 120,
            type: "date",
            valueGetter: ({ row }) => row.DepositDate ? new Date(row.DepositDate) : null,
            renderCell: ({ row }) => (row.DepositDate ? new Date(row.DepositDate).toLocaleDateString("en-US") : "—")
        },
        {
            field: "DepositNumber",
            headerName: "Deposit #",
            flex: 0.6,
            minWidth: 100,
            renderCell: ({ row }) => row.DepositNumber || "—"
        },
        {
            field: "isReconciled",
            headerName: "Reconciled",
            flex: 0.5,
            minWidth: 100,
            type: "boolean",
            renderCell: ({ row }) => row.isReconciled ? <CheckCircleIcon color="success" fontSize="small" /> : null
        },
        {
            field: "actions",
            headerName: "Actions",
            flex: 0.5,
            minWidth: 80,
            sortable: false,
            filterable: false,
            disableExport: true,
            renderCell: ({ row }) => (
                <Tooltip title="Delete">
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(row.apiid)}
                        onMouseDown={() => haptic.light()}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )
        }
    ], [schools, cats, subcats, progs, handleDelete]);

    const ice = tokens(theme.palette.mode).greenAccent?.[400] || "#4cceac";
    const iceSoft = tokens(theme.palette.mode).greenAccent?.[200] || "#70d8bd";
    const instructionSx = {
        background: `linear-gradient(135deg, ${alpha(ice, 0.18)} 0%, ${alpha(iceSoft, 0.18)} 100%)`,
        border: `1px solid ${ice}`,
        boxShadow: `0 0 24px ${alpha(ice, 0.45)}`,
        borderRadius: 2,
        p: 2,
        mb: 2
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box m="20px">
                <Header title="INCOME" subtitle="Financial Management" />

                <Box sx={instructionSx}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <InfoOutlinedIcon />
                        <Typography variant="h6">How to use this page</Typography>
                    </Box>
                    <Divider sx={{ opacity: 0.3, mb: 1 }} />
                    <List dense sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 4 }}>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemText primary="Filter" secondary="Use School Year, School, Month, Category, Subcategory, and Date Range to limit the grid. All filters work together." />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemText primary="Add Income" secondary='Click "Add Income" to open the form. Required: Site, Category/Subcategory, Amount, and Date.' />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemText primary="Scan Import" secondary='Click "Scan Import" to upload a PDF/JPG/PNG/TIFF of an income/reimbursement page. The AI will propose rows; review and save.' />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemText primary="Inline Edit" secondary="Edit cells directly (Site/Category/Subcategory/Amount/Date/Program/Description). IDs auto-sync." />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemText primary="Export" secondary="Use the toolbar export button to download data as CSV or print." />
                        </ListItem>
                    </List>
                </Box>

                <Stack direction="row" spacing={2} mb={2}>
                    <Box
                        sx={{
                            flex: 1,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: alpha(colors.greenAccent[500], 0.1),
                            border: `1px solid ${alpha(colors.greenAccent[500], 0.3)}`
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                        <Typography variant="h4" fontWeight="bold">{usd(totalAmount)}</Typography>
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: alpha(colors.blueAccent[500], 0.1),
                            border: `1px solid ${alpha(colors.blueAccent[500], 0.3)}`
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">Filtered Records</Typography>
                        <Typography variant="h4" fontWeight="bold">{filtered.length}</Typography>
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: alpha(colors.redAccent[500], 0.1),
                            border: `1px solid ${alpha(colors.redAccent[500], 0.3)}`
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">Selected Rows</Typography>
                        <Typography variant="h4" fontWeight="bold">{selectedRows.length}</Typography>
                    </Box>
                </Stack>

                <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
                    <FormControl sx={{ minWidth: 180 }}>
                        <InputLabel>School Year</InputLabel>
                        <Select
                            label="School Year"
                            value={syStart}
                            onChange={(e) => { setSyStart(Number(e.target.value)); haptic.light(); }}
                        >
                            {availableSYStarts.map((y) => (
                                <MenuItem key={y} value={y}>{syLabel(y)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>School</InputLabel>
                        <Select
                            value={schoolFilter}
                            onChange={(e) => {
                                console.log("🏫 School filter changed to:", e.target.value);
                                setSchoolFilter(e.target.value);
                                haptic.selection();
                            }}
                            label="School"
                        >
                            <MenuItem value="">All Schools</MenuItem>
                            {schools.map((s) => (
                                <MenuItem key={s.apiid} value={s.apiid}>
                                    {s.SchoolName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 150 }}>
                        <InputLabel>Month</InputLabel>
                        <Select
                            value={monthFilter}
                            onChange={(e) => { setMonthFilter(e.target.value); haptic.selection(); }}
                            label="Month"
                        >
                            <MenuItem value="">All</MenuItem>
                            {months.map((m) => (<MenuItem key={m} value={m}>{m}</MenuItem>))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 180 }}>
                        <InputLabel>Category</InputLabel>
                        <Select
                            value={categoryFilter}
                            onChange={(e) => handleCategoryChange(e.target.value)}
                            label="Category"
                        >
                            <MenuItem value="">All</MenuItem>
                            {cats.map((c) => (<MenuItem key={c.CategoryID} value={c.CategoryID}>{c.CategoryName}</MenuItem>))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 180 }}>
                        <InputLabel>Subcategory</InputLabel>
                        <Select
                            value={subcategoryFilter}
                            onChange={(e) => { setSubcategoryFilter(e.target.value); haptic.selection(); }}
                            label="Subcategory"
                            disabled={!categoryFilter}
                        >
                            <MenuItem value="">All</MenuItem>
                            {availableSubcats.map((sc) => (
                                <MenuItem key={sc.SubcategoryID} value={sc.SubcategoryID}>{sc.SubcategoryName}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <DatePicker
                        label="From Date"
                        value={dateFrom}
                        onChange={(newValue) => { setDateFrom(newValue); haptic.light(); }}
                        slotProps={{ textField: { size: "small", sx: { minWidth: 150 } } }}
                    />

                    <DatePicker
                        label="To Date"
                        value={dateTo}
                        onChange={(newValue) => { setDateTo(newValue); haptic.light(); }}
                        slotProps={{ textField: { size: "small", sx: { minWidth: 150 } } }}
                    />

                    {activeFilterCount > 0 && (
                        <Badge badgeContent={activeFilterCount} color="primary">
                            <Button
                                variant="outlined"
                                startIcon={<ClearIcon />}
                                onClick={clearFilters}
                                onMouseDown={() => haptic.medium()}
                                size="small"
                            >
                                Clear Filters
                            </Button>
                        </Badge>
                    )}

                    <Box flexGrow={1} />

                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<DescriptionIcon />}
                            onClick={() => { setScanOpen(true); haptic.medium(); }}
                            onMouseDown={() => haptic.light()}
                        >
                            Scan Import
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => { setDepositOpen(true); haptic.medium(); }}
                            onMouseDown={() => haptic.light()}
                            disabled={!filtered.length}
                        >
                            Create Deposit
                        </Button>

                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<AddIcon />}
                            onClick={() => { setEntryOpen(true); haptic.medium(); }}
                            onMouseDown={() => haptic.light()}
                        >
                            Add Income
                        </Button>
                    </Stack>
                </Box>

                {loading ? (
                    <CircularProgress />
                ) : error ? (
                    <Typography color="error">{error}</Typography>
                ) : (
                    <Box
                        height="65vh"
                        sx={{
                            "& .MuiDataGrid-root": { border: "none" },
                            "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: alpha(colors.primary[400], 0.9),
                                borderBottom: `2px solid ${colors.primary[300]}`
                            },
                            "& .MuiDataGrid-row:nth-of-type(even)": {
                                backgroundColor: theme.palette.mode === "light" ? theme.palette.action.hover : colors.primary[600]
                            },
                            "& .MuiDataGrid-row:nth-of-type(odd)": {
                                backgroundColor: theme.palette.mode === "light" ? theme.palette.background.paper : lighten(colors.primary[600], 0.3)
                            },
                            "& .MuiDataGrid-row:hover": {
                                backgroundColor: alpha(colors.greenAccent[500], 0.1)
                            },
                            "& .amount-cell": {
                                fontWeight: "bold",
                                color: colors.greenAccent[400]
                            },
                            "& .MuiDataGrid-cell--editing": {
                                bgcolor: alpha(colors.blueAccent[500], 0.1)
                            }
                        }}
                    >
                        <DataGridPro
                            rows={filtered}
                            columns={mainCols}
                            getRowId={(r) => r.apiid}
                            pagination
                            pageSizeOptions={[10, 25, 50, 100]}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 25 } }
                            }}
                            checkboxSelection
                            disableSelectionOnClick
                            density="compact"
                            onCellEditCommit={handleCellEditCommit}
                            processRowUpdate={processRowUpdate}
                            onProcessRowUpdateError={(e) => { setError(e.message || "Row validation failed"); haptic.error(); }}
                            onRowSelectionModelChange={(newSelection) => {
                                setSelectedRows(newSelection);
                                if (newSelection.length !== selectedRows.length) haptic.selection();
                            }}
                            rowSelectionModel={selectedRows}
                            slots={{
                                toolbar: CustomToolbar
                            }}
                            pinnedColumns={pinnedColumns}
                            onPinnedColumnsChange={setPinnedColumns}
                            getRowClassName={(params) =>
                                params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row'
                            }
                        />
                    </Box>
                )}

                <Modal
                    open={entryOpen}
                    onClose={() => { setEntryOpen(false); haptic.light(); }}
                    sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
                >
                    <Box
                        sx={{
                            position: "relative",
                            width: "80%",
                            height: "80%",
                            bgcolor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                            p: 2,
                            borderRadius: 2,
                            boxShadow: 24,
                            overflow: "auto",
                            border: `1px solid ${ice}`
                        }}
                    >
                        <IconButton
                            onClick={() => { setEntryOpen(false); haptic.light(); }}
                            sx={{ position: "absolute", top: 8, right: 8 }}
                            onMouseDown={() => haptic.light()}
                        >
                            <CloseIcon />
                        </IconButton>
                        <IncomeEntry
                            onClose={() => { setEntryOpen(false); haptic.light(); }}
                            onSaved={() => { setEntryOpen(false); loadIncomes(); haptic.success(); }}
                        />
                    </Box>
                </Modal>

                <GroupDepositDialog
                    open={depositOpen}
                    onClose={() => { setDepositOpen(false); haptic.light(); }}
                    rows={filtered}
                    onSaved={() => { setDepositOpen(false); loadIncomes(); haptic.success(); }}
                />

                <ScanImportDialog
                    open={scanOpen}
                    onClose={() => { setScanOpen(false); haptic.light(); }}
                    cats={cats}
                    subcats={subcats}
                    schools={schools}
                    progs={progs}
                    onSaved={() => { setScanOpen(false); loadIncomes(); haptic.success(); }}
                />
            </Box>
        </LocalizationProvider>
    );
}

function ScanImportDialog({ open, onClose, cats, subcats, schools, progs, onSaved }) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [preview, setPreview] = useState("");
    const [diMeta, setDiMeta] = useState(null);
    const [rows, setRows] = useState([]);

    useEffect(() => {
        if (!open) {
            setFile(null); setBusy(false); setErr(""); setPreview(""); setDiMeta(null); setRows([]);
        } else {
            haptic.medium();
        }
    }, [open]);

    const proposedCols = [
        { field: "SiteName", headerName: "Site", flex: 1, editable: true, type: "singleSelect", valueOptions: schools.map((s) => s.SchoolName) },
        { field: "IncomeCategory", headerName: "Category", flex: 1, editable: true, type: "singleSelect", valueOptions: cats.map((c) => c.CategoryName) },
        {
            field: "IncomeSubcategory",
            headerName: "Subcategory",
            flex: 1,
            editable: true,
            type: "singleSelect",
            valueOptions: (params) => {
                const cat = cats.find((c) => c.CategoryName === params.row.IncomeCategory);
                return subcats.filter((sc) => sc.CategoryID === cat?.CategoryID).map((sc) => sc.SubcategoryName);
            }
        },
        { field: "Amount", headerName: "Amount", flex: 0.8, editable: true, valueFormatter: ({ value }) => usd(value) },
        { field: "IncomeDate", headerName: "Date", flex: 0.8, editable: true, valueGetter: ({ row }) => (row.IncomeDate ? new Date(row.IncomeDate).toLocaleDateString("en-US") : "") },
        { field: "Description", headerName: "Description", flex: 1.2, editable: true },
        { field: "ReimbursementTrackingProgram", headerName: "Reimb Prog", flex: 0.8, editable: true, type: "singleSelect", valueOptions: ["", ...progs.map((p) => p.program_abbreviation).filter(Boolean)] }
    ];

    const mapNamesToIds = (draft) => {
        const out = { ...draft };
        const school = schools.find((s) => s.SchoolName?.trim().toLowerCase() === (draft.SiteName || "").trim().toLowerCase());
        if (school) { out.SchoolId = school.apiid; out.SiteName = school.SchoolName; }
        const cat = cats.find((c) => c.CategoryName?.trim().toLowerCase() === (draft.IncomeCategory || "").trim().toLowerCase());
        if (cat) { out.IncomeCategoryID = cat.CategoryID; out.IncomeCategory = cat.CategoryName; }
        if (out.IncomeCategoryID) {
            const sub = subcats.find((s) =>
                s.SubcategoryName?.trim().toLowerCase() === (draft.IncomeSubcategory || "").trim().toLowerCase() &&
                s.CategoryID === out.IncomeCategoryID
            );
            if (sub) { out.IncomeSubCategoryID = sub.SubcategoryID; out.IncomeSubcategory = sub.SubcategoryName; }
            else { out.IncomeSubCategoryID = null; out.IncomeSubcategory = ""; }
        } else { out.IncomeSubCategoryID = null; out.IncomeSubcategory = ""; }
        out.Amount = parseMaybeNumber(draft.Amount);
        out.IncomeDate = normalizeDate(draft.IncomeDate || draft.Date || draft.PostingDate);
        out.Description = (draft.Description || "").trim();
        const progNames = progs.map((p) => p.program_abbreviation).filter(Boolean);
        if (!progNames.includes(out.ReimbursementTrackingProgram || "")) out.ReimbursementTrackingProgram = "";
        return out;
    };

    const buildDescriptionFallback = (di) => {
        if (!di) return "";
        const kv = di.keyValuePairs || [];
        const district = kv.find((k) => /district|school\s*district/i.test(k.key || ""))?.value;
        const month = (di.preview || "").match(/\b(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\b/i)?.[0];
        const snippet = (di.preview || "").slice(0, 80).replace(/\s+/g, " ").trim();
        return `Scanned income${month ? ` (${month})` : ""}${district ? ` - ${district}` : ""}${snippet ? ` — ${snippet}` : ""}`;
    };

    const runDIThenAI = async () => {
        if (!file) { setErr("Please choose a PDF/JPG/PNG/TIFF."); haptic.error(); return; }
        setBusy(true); setErr(""); haptic.medium();
        try {
            const form = new FormData();
            form.append("file", file);
            form.append("includeContent", "1");
            form.append("keepPages", "all");
            const diRes = await fetch(API_DI_EXTRACT, { method: "POST", body: form });
            if (!diRes.ok) { const t = await diRes.text(); throw new Error(`DI failed ${diRes.status}: ${t}`); }
            const di = await diRes.json();
            setDiMeta(di);
            setPreview(di.preview || "");
            haptic.light();

            const sys =
                "You are a finance ingestion assistant. You receive OCR'd document intelligence (tables, key value pairs, page text) for *income* or *reimbursement* pages from school nutrition. " +
                "Your task: detect the incomes on the page and return STRICT JSON. Choose the best Category/Subcategory from the provided lists; " +
                "Subcategory MUST belong to the chosen Category (match by CategoryID). " +
                "Determine if multiple rows are needed (e.g., different programs/categories or sites). " +
                "Amounts should be numeric. If unsure of site, choose a best guess by name; otherwise leave a blank string. " +
                "For Description, include a short human-friendly label (e.g., 'Reimbursement income – Lunch, August 2025, Calhoun 014'). " +
                "If you see counts/tables for BR/LN/SN/Supper or 'Edit Check' style, treat it as reimbursement income, grouping appropriately.";

            const payload = {
                messages: [
                    { role: "system", content: sys },
                    {
                        role: "user",
                        content: JSON.stringify({
                            di: { preview: di.preview, tables: di.tables, keyValuePairs: di.keyValuePairs, pages: (di.pages || []).slice(0, 3) },
                            categories: cats.map((c) => ({ CategoryID: c.CategoryID, CategoryName: c.CategoryName })),
                            subcategories: subcats.map((s) => ({ SubcategoryID: s.SubcategoryID, SubcategoryName: s.SubcategoryName, CategoryID: s.CategoryID })),
                            schools: schools.map((s) => ({ apiid: s.apiid, SchoolName: s.SchoolName })),
                            want_schema:
                                "Return ONLY JSON: { rows: [ { SiteName, IncomeCategory, IncomeCategoryID, IncomeSubcategory, IncomeSubCategoryID, Amount, IncomeDate, Description, ReimbursementTrackingProgram } ] }"
                        })
                    }
                ],
                temperature: 0.1,
                max_tokens: 1400
            };

            const aiRes = await fetch(API_CHAT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });
            if (!aiRes.ok) { const t = await aiRes.text(); throw new Error(`AI classify failed ${aiRes.status}: ${t}`); }
            const aiJson = await aiRes.json();
            const text = aiJson?.message?.content || "";
            const parsed = stripJson(text);
            let proposed = Array.isArray(parsed?.rows) ? parsed.rows : [];

            if (!proposed.length) {
                proposed = [{
                    SiteName: "", IncomeCategory: "", IncomeSubcategory: "", Amount: 0, IncomeDate: "",
                    Description: buildDescriptionFallback(di), ReimbursementTrackingProgram: ""
                }];
            }

            const normalized = proposed
                .map((r, idx) => ({ id: `new-${idx + 1}`, ...mapNamesToIds(r) }))
                .map((r) => ({
                    ...r,
                    SiteName: r.SiteName || "",
                    IncomeCategory: r.IncomeCategory || "",
                    IncomeSubcategory: r.IncomeSubcategory || "",
                    Description: r.Description || buildDescriptionFallback(di)
                }));

            setRows(normalized);
            haptic.success();
        } catch (e) {
            console.error(e);
            setErr(e.message || "Import failed");
            haptic.error();
        } finally {
            setBusy(false);
        }
    };

    const updateRow = (id, patch) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

    const handleProposedEditCommit = (params) => {
        const { id, field, value } = params;
        const patch = {};
        haptic.light();

        if (field === "SiteName") {
            const school = schools.find((s) => s.SchoolName === value);
            patch.SiteName = value || "";
            patch.SchoolId = school?.apiid || null;
        } else if (field === "IncomeCategory") {
            const cat = cats.find((c) => c.CategoryName === value);
            patch.IncomeCategory = value || "";
            patch.IncomeCategoryID = cat?.CategoryID || null;
            patch.IncomeSubcategory = "";
            patch.IncomeSubCategoryID = null;
        } else if (field === "IncomeSubcategory") {
            const cur = rows.find((r) => r.id === id);
            const sub = subcats.find((s) => s.SubcategoryName === value && s.CategoryID === cur?.IncomeCategoryID);
            if (sub) { patch.IncomeSubcategory = sub.SubcategoryName; patch.IncomeSubCategoryID = sub.SubcategoryID; }
            else { patch.IncomeSubcategory = ""; patch.IncomeSubCategoryID = null; }
        } else if (field === "Amount") {
            patch.Amount = parseMaybeNumber(value);
        } else if (field === "IncomeDate") {
            patch.IncomeDate = normalizeDate(value);
        } else { patch[field] = value; }
        updateRow(id, patch);
    };

    const saveAll = async () => {
        const missingSite = rows.filter((r) => !(r.SchoolId && r.SiteName?.trim()));
        const missingCat = rows.filter((r) => !(r.IncomeCategoryID && r.IncomeCategory?.trim()));
        const missingSub = rows.filter(
            (r) => r.IncomeCategoryID && r.IncomeSubcategory &&
                !subcats.find((s) => s.SubcategoryName === r.IncomeSubcategory && s.CategoryID === r.IncomeCategoryID)
        );
        if (missingSite.length) { setErr("Each row must have a valid Site."); haptic.error(); return; }
        if (missingCat.length) { setErr("Each row must have a valid Category."); haptic.error(); return; }
        if (missingSub.length) { setErr("Each Subcategory must belong to its Category."); haptic.error(); return; }

        setBusy(true); setErr(""); haptic.medium();
        try {
            for (const r of rows) {
                const body = {
                    SchoolId: r.SchoolId, SiteName: r.SiteName,
                    IncomeCategory: r.IncomeCategory, IncomeCategoryID: r.IncomeCategoryID,
                    IncomeSubcategory: r.IncomeSubcategory || "", IncomeSubCategoryID: r.IncomeSubCategoryID || null,
                    Amount: parseMaybeNumber(r.Amount), IncomeDate: r.IncomeDate || null,
                    Description: (r.Description || "").trim(),
                    ReimbursementTrackingProgram: r.ReimbursementTrackingProgram || ""
                };
                const res = await fetch(API_INCOMES, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
                if (!res.ok) { const t = await res.text(); throw new Error(`Insert failed: ${res.status} ${t}`); }
            }
            haptic.success();
            onSaved();
        } catch (e) {
            console.error(e);
            setErr(e.message || "Save failed");
            haptic.error();
        } finally { setBusy(false); }
    };

    return (
        <Modal open={open} onClose={onClose} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Box
                sx={{
                    position: "relative",
                    width: "92%",
                    maxWidth: 1200,
                    height: "88%",
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    p: 2,
                    borderRadius: 2,
                    boxShadow: 24,
                    overflow: "hidden",
                    border: `1px solid ${tokens(theme.palette.mode).greenAccent?.[400] || "#4cceac"}`
                }}
            >
                <IconButton
                    onClick={onClose}
                    sx={{ position: "absolute", top: 8, right: 8 }}
                    onMouseDown={() => haptic.light()}
                >
                    <CloseIcon />
                </IconButton>

                <Typography variant="h6" sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                    <UploadIcon fontSize="small" /> Scan Import (DI prebuilt-document → AI classify)
                </Typography>

                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <Button
                        variant="contained"
                        color="secondary"
                        component="label"
                        startIcon={<UploadIcon />}
                        onMouseDown={() => haptic.light()}
                    >
                        Choose File
                        <input type="file" hidden accept=".pdf,image/*" onChange={(e) => { setFile(e.target.files?.[0] || null); haptic.light(); }} />
                    </Button>
                    <Typography variant="body2">{file ? file.name : "PDF/JPG/PNG/TIFF"}</Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={runDIThenAI}
                        disabled={!file || busy}
                        onMouseDown={() => haptic.light()}
                    >
                        {busy ? <CircularProgress size={18} color="inherit" /> : "Analyze & Classify"}
                    </Button>
                </Stack>

                {err && <Alert severity="error" sx={{ mb: 1 }}>{err}</Alert>}

                <Box display="grid" gridTemplateColumns="1.2fr 2fr" gap={2} height="calc(100% - 110px)">
                    <Box sx={{ border: `1px dashed ${colors.grey[400]}`, borderRadius: 1, p: 1, overflow: "auto", minHeight: 120 }}>
                        <Typography variant="subtitle2" gutterBottom>DI Preview</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{preview || "—"}</Typography>
                        {diMeta?.tables?.length ? (
                            <Box mt={1}>
                                <Typography variant="subtitle2">Detected Tables: {diMeta.tables.length}</Typography>
                                <Typography variant="caption" color="text.secondary">(Prebuilt-document; "Edit Check" rescue heuristic kicks in if DI misses tables)</Typography>
                            </Box>
                        ) : null}
                    </Box>

                    <Box sx={{ "& .MuiDataGrid-root": { border: "none" }, border: `1px solid ${alpha(colors.greenAccent?.[400] || "#4cceac", 0.6)}`, borderRadius: 1, overflow: "hidden" }}>
                        <DataGridPro
                            rows={rows}
                            columns={proposedCols}
                            getRowId={(r) => r.id}
                            density="compact"
                            onCellEditCommit={handleProposedEditCommit}
                            hideFooter
                        />
                    </Box>
                </Box>

                <Box mt={1} display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                        Tip: Subcategories are constrained to the selected Category. Choose Site to link SchoolId.
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={onClose}
                            onMouseDown={() => haptic.light()}
                        >
                            Close
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            disabled={!rows.length || busy}
                            onClick={saveAll}
                            onMouseDown={() => haptic.light()}
                        >
                            {busy ? <CircularProgress size={18} color="inherit" /> : `Save ${rows.length} Row${rows.length === 1 ? "" : "s"}`}
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Modal>
    );
}

function GroupDepositDialog({ open, onClose, rows, onSaved }) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [sel, setSel] = useState([]);
    const [depositNumber, setDepositNumber] = useState("");
    const [depositDate, setDepositDate] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            setSel([]);
            setDepositDate("");
            setDepositNumber("");
            setError("");
            haptic.medium();
        }
    }, [open]);

    const total = sel.reduce((sum, id) => {
        const r = rows.find((x) => x.apiid === id);
        return sum + parseFloat(r?.Amount || 0);
    }, 0);

    async function saveDeposit() {
        const bad = sel.map((id) => rows.find((x) => x.apiid === id)).filter((r) => !r?.SiteName || !r.SiteName.trim());
        if (bad.length) { setError("All selected rows must have a Site before creating a deposit."); haptic.error(); return; }

        setSaving(true); haptic.medium();
        try {
            const depositId =
                typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-dep`;

            const mstRes = await fetch(API_DEPOSIT_MST, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ apiid: depositId, DepositNumber: depositNumber, Amount: total, DepositDate: depositDate })
            });
            if (!mstRes.ok) throw new Error("Failed to create deposit master");

            for (let id of sel) {
                const r = rows.find((x) => x.apiid === id);
                const detRes = await fetch(API_DEPOSIT_DETAILS, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        depositid: depositId,
                        incomeItemId: r.IncomeID,
                        incomeItemDescription: r.Description,
                        CategoryId: parseInt(r.IncomeCategoryID),
                        CategoryName: r.IncomeCategory,
                        SubCategoryId: parseInt(r.IncomeSubCategoryID),
                        itemAmount: r.Amount
                    })
                });
                if (!detRes.ok) throw new Error("Failed to insert deposit detail");

                const putRes = await fetch(`${API_INCOMES}/${id}`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ DepositId: depositId, DepositNumber: depositNumber, DepositDate: depositDate })
                });
                if (!putRes.ok) throw new Error("Failed to update income item");
            }

            haptic.success();
            onSaved();
        } catch (e) {
            console.error("saveDeposit error", e);
            setError(e.message || "Failed to save deposit");
            haptic.error();
        } finally { setSaving(false); }
    }

    return (
        <Modal open={open} onClose={onClose} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Box
                sx={{
                    width: "80%",
                    maxWidth: 820,
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    p: 3,
                    borderRadius: 2,
                    border: `1px solid ${tokens(theme.palette.mode).greenAccent?.[400] || "#4cceac"}`,
                    boxShadow: `0 0 18px ${alpha(tokens(theme.palette.mode).greenAccent?.[400] || "#4cceac", 0.45)}`
                }}
            >
                <Typography variant="h6" mb={2}>Select Income to Group</Typography>

                <Box
                    height={320}
                    sx={{
                        "& .MuiDataGrid-root": { border: "none" },
                        "& .MuiDataGrid-row:nth-of-type(even)": {
                            backgroundColor: theme.palette.mode === "light" ? theme.palette.action.hover : colors.primary[600]
                        },
                        "& .MuiDataGrid-row:nth-of-type(odd)": {
                            backgroundColor: theme.palette.mode === "light" ? theme.palette.background.paper : lighten(colors.primary[600], 0.3)
                        }
                    }}
                >
                    <DataGridPro
                        rows={rows}
                        columns={[
                            { field: "SiteName", headerName: "Site", flex: 1 },
                            { field: "IncomeCategory", headerName: "Category", flex: 1 },
                            { field: "Amount", headerName: "Amount", flex: 0.8, valueFormatter: ({ value }) => usd(value) },
                            { field: "IncomeDate", headerName: "Date", flex: 0.8, valueGetter: ({ row }) => (row.IncomeDate ? new Date(row.IncomeDate).toLocaleDateString("en-US") : "") }
                        ]}
                        getRowId={(r) => r.apiid}
                        checkboxSelection
                        onRowSelectionModelChange={(newSel) => { setSel(newSel); haptic.selection(); }}
                        rowSelectionModel={sel}
                        hideFooter
                        density="standard"
                    />
                </Box>

                <Typography mt={2}>Total: {usd(total)}</Typography>

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                        label="Deposit Date"
                        value={depositDate ? new Date(depositDate) : null}
                        onChange={(newValue) => {
                            setDepositDate(newValue ? newValue.toISOString().slice(0, 10) : "");
                            haptic.light();
                        }}
                        slotProps={{ textField: { fullWidth: true, margin: "dense" } }}
                    />
                </LocalizationProvider>

                <TextField
                    label="Deposit Number"
                    fullWidth
                    margin="dense"
                    value={depositNumber}
                    onChange={(e) => setDepositNumber(e.target.value)}
                />

                {error && <Typography mt={1} color="error">{error}</Typography>}

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={onClose}
                        onMouseDown={() => haptic.light()}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={saveDeposit}
                        disabled={!sel.length || !depositNumber || !depositDate || saving}
                        onMouseDown={() => haptic.light()}
                    >
                        {saving ? <CircularProgress size={18} color="inherit" /> : "Save Deposit"}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}